import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchApontamentos, addApontamento, updateApontamento, deleteApontamento, importarTempoCronometro, minutosParaTexto, ehFaturavel } from '../../../api/worklog';
import './ApontamentosCard.css';

function hoje() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatarData(d) {
    if (!d) return '';
    const s = String(d).slice(0, 10);
    const [y, m, dd] = s.split('-');
    return dd && m && y ? `${dd}/${m}/${y}` : s;
}

const FORM_INICIAL = { id: null, data: hoje(), horas: '', minutos: '', descricao: '', faturavel: true };

const CONTEXTOS = [
    { key: 'atendimento', label: 'Atendimento', visible: (usaSprint) => !usaSprint },
    { key: 'desenvolvimento', label: 'Desenvolvimento', visible: (usaSprint) => !!usaSprint },
    { key: 'analise', label: 'Análise de requisitos', visible: () => true },
    { key: 'teste', label: 'Considerações de teste', visible: () => true },
];

function ApontamentosCard({ satNum, embedded = false, usaSprint = false, tempoOperacional = {} }) {
    const [dados, setDados] = useState({ apontamentos: [], total_minutos: 0, lancado_auto: {} });
    const [loading, setLoading] = useState(true);
    const [mensagem, setMensagem] = useState('');
    const [mensagemOk, setMensagemOk] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(FORM_INICIAL);
    const [salvando, setSalvando] = useState(false);
    const [importando, setImportando] = useState(false);
    const [importarFaturavel, setImportarFaturavel] = useState(true);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetchApontamentos(satNum);
            setDados(d || { apontamentos: [], total_minutos: 0, lancado_auto: {} });
        } finally {
            setLoading(false);
        }
    }, [satNum]);

    useEffect(() => { if (satNum) carregar(); }, [satNum, carregar]);

    const lancadoAuto = dados.lancado_auto || {};
    const pendencias = useMemo(() => {
        return CONTEXTOS
            .filter((c) => c.visible(usaSprint))
            .map((c) => {
                const operacional = Number(tempoOperacional[c.key]) || 0;
                const lancado = Number(lancadoAuto[c.key]) || 0;
                const pendente = Math.max(0, operacional - lancado);
                return { ...c, operacional, lancado, pendente };
            })
            .filter((c) => c.operacional > 0 || c.lancado > 0 || c.pendente > 0);
    }, [usaSprint, tempoOperacional, lancadoAuto]);

    const totalPendente = pendencias.reduce((a, p) => a + p.pendente, 0);

    const abrirNovo = () => { setForm(FORM_INICIAL); setMensagem(''); setShowForm(true); };
    const abrirEdicao = (a) => {
        setForm({
            id: a.id, data: String(a.data).slice(0, 10),
            horas: String(Math.floor((a.minutos || 0) / 60)), minutos: String((a.minutos || 0) % 60),
            descricao: a.descricao || '', faturavel: ehFaturavel(a.faturavel)
        });
        setMensagem('');
        setShowForm(true);
    };

    const salvar = async () => {
        const min = (parseInt(form.horas || 0, 10) * 60) + parseInt(form.minutos || 0, 10);
        if (!min || min <= 0) { setMensagem('Informe o tempo (horas e/ou minutos).'); return; }
        if (!form.data) { setMensagem('Informe a data.'); return; }
        setSalvando(true);
        setMensagem('');
        try {
            if (form.id) {
                await updateApontamento(form.id, { data: form.data, minutos: min, descricao: form.descricao || null, faturavel: form.faturavel });
            } else {
                await addApontamento({ sat_num: satNum, data: form.data, minutos: min, descricao: form.descricao || null, faturavel: form.faturavel, origem: 'manual' });
            }
            setShowForm(false);
            setMensagemOk('Apontamento salvo.');
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao salvar apontamento.');
        } finally {
            setSalvando(false);
        }
    };

    const importarPendentes = async () => {
        const fat = importarFaturavel ? 1 : 0;
        const itens = pendencias
            .filter((p) => p.pendente > 0)
            .map((p) => ({ contexto: p.key, minutos: p.pendente, data: hoje(), faturavel: fat }));
        if (itens.length === 0) { setMensagem('Não há tempo pendente para lançar.'); return; }
        setImportando(true);
        setMensagem('');
        setMensagemOk('');
        try {
            await importarTempoCronometro({ sat_num: satNum, itens, faturavel: fat });
            setMensagemOk(`${itens.length} lançamento(s) importado(s) do cronômetro.`);
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao importar tempo.');
        } finally {
            setImportando(false);
        }
    };

    const remover = async (a) => {
        if (!window.confirm('Remover este apontamento de horas?')) return;
        try { await deleteApontamento(a.id); await carregar(); }
        catch (e) { setMensagem(e?.response?.data?.error || 'Erro ao remover.'); }
    };

    const lista = dados.apontamentos || [];
    const wrapClass = embedded ? 'wl-embedded' : 'container-fluid px-3 wl-card-wrap';
    const cardClass = embedded ? 'wl-card wl-card--embedded' : 'card wl-card';

    return (
        <div className={wrapClass}>
            <div className={cardClass}>
                {!embedded && (
                    <div className="card-header wl-card-header d-flex justify-content-between align-items-center">
                        <span>
                            <i className="fas fa-clock me-2" />Apontamento de horas
                            {dados.total_minutos > 0 && <span className="badge bg-primary ms-2">{minutosParaTexto(dados.total_minutos)}</span>}
                        </span>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirNovo}>
                            <i className="fas fa-plus me-1" />Lançar horas
                        </button>
                    </div>
                )}
                <div className={embedded ? 'wl-embedded-body' : 'card-body'}>
                    {embedded && (
                        <div className="wl-embedded-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <span className="wl-embedded-titulo">
                                <i className="fas fa-clock me-2" />Lançamento de horas
                                {dados.total_minutos > 0 && <span className="badge bg-primary ms-2">{minutosParaTexto(dados.total_minutos)} total</span>}
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirNovo}>
                                <i className="fas fa-plus me-1" />Lançar manualmente
                            </button>
                        </div>
                    )}

                    {mensagemOk && <div className="alert alert-success py-2 mb-2">{mensagemOk}</div>}
                    {loading && <p className="text-muted small mb-0">Carregando...</p>}

                    {!loading && pendencias.length > 0 && (
                        <div className="wl-sync-panel mb-3">
                            <div className="wl-sync-titulo">Tempo do cronômetro vs. lançado</div>
                            <div className="table-responsive">
                                <table className="table table-sm wl-sync-table mb-2">
                                    <thead>
                                        <tr>
                                            <th>Etapa</th>
                                            <th>Cronômetro</th>
                                            <th>Já lançado</th>
                                            <th>Pendente</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendencias.map((p) => (
                                            <tr key={p.key} className={p.pendente > 0 ? 'wl-sync-pendente' : ''}>
                                                <td>{p.label}</td>
                                                <td>{minutosParaTexto(p.operacional)}</td>
                                                <td>{minutosParaTexto(p.lancado)}</td>
                                                <td>{p.pendente > 0 ? <strong>{minutosParaTexto(p.pendente)}</strong> : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPendente > 0 && (
                                <div className="wl-sync-acoes d-flex flex-wrap align-items-center gap-3">
                                    <div className="form-check mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="wl-importar-fat"
                                            checked={importarFaturavel}
                                            onChange={(e) => setImportarFaturavel(e.target.checked)}
                                            disabled={importando}
                                        />
                                        <label className="form-check-label small" htmlFor="wl-importar-fat">Faturável</label>
                                    </div>
                                    <button type="button" className="btn btn-sm btn-primary" disabled={importando} onClick={importarPendentes}>
                                        <i className="fas fa-file-import me-1" />
                                        {importando ? 'Lançando...' : `Lançar tempo pendente (${minutosParaTexto(totalPendente)})`}
                                    </button>
                                </div>
                            )}
                            {totalPendente === 0 && pendencias.some((p) => p.operacional > 0) && (
                                <p className="text-muted small mb-0">Todo o tempo registrado no cronômetro já foi lançado.</p>
                            )}
                        </div>
                    )}

                    {mensagem && !showForm && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}

                    {!loading && lista.length === 0 && (
                        <p className="text-muted small mb-0">Nenhuma hora lançada neste chamado. Use o cronômetro (Play/Pause) e importe o tempo pendente, ou lance manualmente.</p>
                    )}

                    {!loading && lista.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-sm wl-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Data</th><th>Colaborador</th><th>Tempo</th><th>Etapa</th><th>Descrição</th><th className="text-center">Fat.</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lista.map((a) => (
                                        <tr key={a.id}>
                                            <td className="text-nowrap">{formatarData(a.data)}</td>
                                            <td className="text-nowrap">{a.colaborador || '—'}</td>
                                            <td className="text-nowrap">{minutosParaTexto(a.minutos)}</td>
                                            <td className="text-nowrap small">
                                                {a.contexto ? (
                                                    <span className={`badge ${a.origem === 'auto' ? 'bg-info text-dark' : 'bg-secondary'}`}>
                                                        {CONTEXTOS.find((c) => c.key === a.contexto)?.label || a.contexto}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="wl-desc">{a.descricao || '—'}</td>
                                            <td className="text-center">{ehFaturavel(a.faturavel) ? <i className="fas fa-check text-success" title="Faturável" /> : <i className="fas fa-minus text-muted" title="Não faturável" />}</td>
                                            <td className="text-nowrap text-end">
                                                {a.origem !== 'auto' ? (
                                                    <>
                                                        <button type="button" className="btn btn-sm btn-link p-1" title="Editar" onClick={() => abrirEdicao(a)}><i className="fas fa-pen" /></button>
                                                        <button type="button" className="btn btn-sm btn-link p-1 text-danger" title="Remover" onClick={() => remover(a)}><i className="fas fa-trash" /></button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="text-muted small me-1" title="Importado do cronômetro">auto</span>
                                                        <button type="button" className="btn btn-sm btn-link p-1 text-danger" title="Remover" onClick={() => remover(a)}><i className="fas fa-trash" /></button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <div className="modal-overlay" onClick={() => !salvando && setShowForm(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-clock me-2" />{form.id ? 'Editar apontamento' : 'Lançar horas'}</h5>
                        <div className="row g-2 mb-2">
                            <div className="col-6">
                                <label className="small">Data</label>
                                <input type="date" className="form-control form-control-sm" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
                            </div>
                            <div className="col-3">
                                <label className="small">Horas</label>
                                <input type="number" min="0" className="form-control form-control-sm" value={form.horas} onChange={(e) => setForm({ ...form, horas: e.target.value })} placeholder="0" />
                            </div>
                            <div className="col-3">
                                <label className="small">Min</label>
                                <input type="number" min="0" max="59" className="form-control form-control-sm" value={form.minutos} onChange={(e) => setForm({ ...form, minutos: e.target.value })} placeholder="0" />
                            </div>
                        </div>
                        <div className="form-group mb-2">
                            <label className="small">Descrição</label>
                            <input className="form-control form-control-sm" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="O que foi feito" />
                        </div>
                        <div className="form-check mb-2">
                            <input className="form-check-input" type="checkbox" id="wl-fat" checked={form.faturavel} onChange={(e) => setForm({ ...form, faturavel: e.target.checked })} />
                            <label className="form-check-label small" htmlFor="wl-fat">Faturável</label>
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={salvando} onClick={() => setShowForm(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando...' : 'Salvar'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApontamentosCard;
